import { Box, CircularProgress } from "@mui/material";
import { useMemo } from "react";

import { IncomingCall } from "./incoming-call/IncomingCall";
import { InCall } from "./in-call/InCall";
import { DialerCall } from "./dialer-call/DialerCall";
import { InternalOutgoingCall } from "./outgoing-call/InternalOutgoingCall";
import { useVirtualAssistant } from "../context/VirtualAssistantContext";
import { useOptionalInternalWebRTC } from "../../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalInternalTwilioCall } from "../../internal-twilio-call/context/InternalTwilioCallContext";
import { useOptionalCallArbitration } from "./arbitration/CallArbitrationContext";
import { AlternatingCallsView } from "./arbitration/AlternatingCallsView";
import { AccessDecision, AccessGate } from "src/permissions/access";
import { FlowMobileLockedBehavior } from "src/permissions/access/behaviors/FlowMobileBejavior";

const ENABLE_IN_CALL_MOCK = false;

export const CallWidget = ({ hasFlowMobileNumber }: { hasFlowMobileNumber: boolean }) => {
  const { companiesState } = useVirtualAssistant();
  const internalWebRTC = useOptionalInternalWebRTC();
  const internalTwilio = useOptionalInternalTwilioCall();
  const arbitration = useOptionalCallArbitration();

  const flowMobileAccess: AccessDecision = {
    granted: hasFlowMobileNumber,
    reason: "FLOW_MOBILE_REQUIRED",
  };

  // Twilio internal and LiveKit internal share the same Contact Center screens.
  const internalView =
    internalTwilio?.internalCallView ?? internalWebRTC?.internalCallView ?? null;

  const activeCompanyCall = useMemo(() => {
    if (ENABLE_IN_CALL_MOCK) {
      return {
        key: "106:+15551234567",
        companyId: "106",
        view: "in-call" as const,
      };
    }

    const companyEntries = Array.from(companiesState.entries());

    const incomingEntry = companyEntries.find(
      ([_, item]) => item.activeCall && item.callDirection === "INCOMING",
    );

    if (incomingEntry) {
      const [key, state] = incomingEntry;
      return {
        key,
        companyId: state.companyId,
        view: "incoming" as const,
      };
    }

    const inCallEntry = companyEntries.find(
      ([_, item]) => item.activeCall && item.callDirection !== "INCOMING",
    );

    if (inCallEntry) {
      const [key, state] = inCallEntry;
      return {
        key,
        companyId: state.companyId,
        view: "in-call" as const,
      };
    }

    return null;
  }, [companiesState]);

  const renderInternalCallView = () => {
    if (!internalView) return null;

    const content =
      internalView === "incoming" ? (
        <IncomingCall />
      ) : internalView === "outgoing-ringing" ? (
        <InternalOutgoingCall />
      ) : internalView === "in-call" ? (
        <InCall />
      ) : null;

    return (
      <Box
        sx={{ height: "100%", overflow: "hidden" }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={2}
      >
        {content ?? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  if (arbitration?.alternating) {
    return (
      <Box
        sx={{ height: "100%", overflow: "hidden" }}
        display="flex"
        flexDirection="column"
        p={2}
      >
        <AlternatingCallsView />
      </Box>
    );
  }

  const suppressInternalIncoming =
    arbitration?.showChoice && internalView === "incoming";

  if (internalView && !suppressInternalIncoming) {
    return renderInternalCallView();
  }

  const renderActiveCallView = () => {
    if (!activeCompanyCall) return null;

    return (
      <Box
        sx={{ height: "100%", overflow: "hidden" }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={2}
      >
        {activeCompanyCall.view === "incoming" ? (
          <IncomingCall deviceKey={activeCompanyCall.key} />
        ) : (
          <InCall deviceKey={activeCompanyCall.key} />
        )}
      </Box>
    );
  };

  if (activeCompanyCall) {
    return renderActiveCallView();
  }

  return (
    <Box
      flex={1}
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={2}
      sx={{ overflowY: "auto" }}
    >

      <AccessGate
        strategy={flowMobileAccess}
        behavior={FlowMobileLockedBehavior}
        behaviorConfig={{
          message: "Activate Flow Mobile to use the dialer",
        }}
      >
        <DialerCall />
      </AccessGate>
    </Box>
  );
};
